import { exportQuotation } from './../../features/quotations/store/quotations.actions';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { CurrentUser } from 'src/app/features/auth/authentication/models/auth-response';
import { AddBrandsComponent } from 'src/app/features/brands/components/add-brands/add-brands.component';
import { SetBrandSearch } from 'src/app/features/brands/store/brands.actions';
import { CatalogsFilterComponent } from 'src/app/features/catalogs/components/catalogs-filter/catalogs-filter.component';
import { ConfirmNotificationComponent } from 'src/app/features/catalogs/components/confirm-notification/confirm-notification.component';
import { ShareCatalogComponent } from 'src/app/features/catalogs/components/share-catalog/share-catalog.component';
import { ICatalogFilterRequest } from 'src/app/features/catalogs/models/catalogs.models';
import { SetCatalogsFilter } from 'src/app/features/catalogs/store/catalogs.actions';
import { AddCategoriesComponent } from 'src/app/features/categories/components/add-categories/add-categories.component';
import { SetCategoriesSearch } from 'src/app/features/categories/store/categories.actions';
import { FilterGroupComponent } from 'src/app/features/companies-groups/components/filter-group/filter-group.component';
import { IGroupFilterRequest } from 'src/app/features/companies-groups/models/companies-groups.model';
import { SetGroupFilter } from 'src/app/features/companies-groups/store/companies-groups.actions';
import { ImportComparatorsComponent } from 'src/app/features/comparator/components/import-comparators/import-comparators.component';
import { CustomersFiltersComponent } from 'src/app/features/customers/components/customers-filters/customers-filters.component';
import { ICompanyFilterRequest } from 'src/app/features/customers/models/customers.models';
import { exportCompanies, SetCompanyFilter } from 'src/app/features/customers/store/customers.actions';
import { ImportFilterComponent } from 'src/app/features/imports/components/import-filter/import-filter.component';
import { ImportComponent } from 'src/app/features/imports/components/import/import.component';
import { IImportFilterRequest } from 'src/app/features/imports/models/imports.models';
import { SetImportFilter } from 'src/app/features/imports/store/imports.actions';
import { OrdersFilterComponent } from 'src/app/features/orders/components/orders-filter/orders-filter.component';
import { IInboundChannel, IOrder, IOrderFilterRequest } from 'src/app/features/orders/models/order.model';
import { exportOrders, SetOrdersFilter, ToggleProcessingVisibility } from 'src/app/features/orders/store/orders.actions';
import { InboundChannelService } from 'src/app/features/orders/services/inbound-channel.service';
import { AtoProcessingStatus } from 'src/app/features/quotations/enum/quotations.status.enum';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { IInventoryFilterRequest } from 'src/app/features/produits/models/products.models';
import { exportProducts, SetInventoryFilter } from 'src/app/features/produits/store/produits.actions';
import { FilterQuotationsComponent } from 'src/app/features/quotations/components/filter-quotations/filter-quotations.component';
import { IQuotation, IQuotationFilterRequest } from 'src/app/features/quotations/models/quotations.models';
import { SetIsCopilot, SetQuotationFilter } from 'src/app/features/quotations/store/quotations.actions';
import { AddSegmentsComponent } from 'src/app/features/segments/components/add-segments/add-segments.component';
import { FilterSegmentComponent } from 'src/app/features/segments/components/filter-segment/filter-segment.component';
import { ISegmentFilterRequest } from 'src/app/features/segments/models/segment.model';
import { SetSegmentFilter } from 'src/app/features/segments/store/segments.actions';
import { SetSupplierSearch } from 'src/app/features/suppliers/store/suppliers.actions';
import { AddTagsComponent } from 'src/app/features/tags/components/add-tags/add-tags.component';
import { SetTagSearch } from 'src/app/features/tags/store/tags.actions';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { NavigationService } from 'src/app/shared/router.service';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';
import { SetCurrentElementId, SetCurrentElementObject } from 'src/app/shared/store/shared.actions';
import {
  encodeFilterObject,
  parseCompanyFilterRequest,
  parseFilterRequest,
  parseInventoryFilterRequest,
  parseOrdersFilterRequest,
  parseQuotationFilterRequest,
  refillCatalogsFilter,
  refillCompanyFilter,
  refillFilter,
  refillGroupFilter,
  refillImportsFilter,
  refillInventoryFilter,
  refillOrdersFilter,
  refillQuotationFilter,
  refillSegmentFilter,
} from 'src/app/shared/utils/helpers';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  currentId!: number;
  currentName!: string;
  currentCompanyId!: string;
  currentCheck!: boolean;
  totalElement: number = 0;
  previousUrl: string | undefined = undefined;
  currentUrl: string | undefined = undefined;
  currentMetaData$!: Observable<IPagination | null>;
  navbarParams: { title: string | null; breadcrumbs: { label: string; url: string | null }[] | []; parentPath: string | null; icon: string | null };
  currentUser!: CurrentUser;
  isCopilot$: Observable<boolean> = of(false);
  isCopilotTitle!: string;
  isCopilot!: boolean;
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  // ── Inbound order channel control ────────────────────────────────────────
  inboundChannel: IInboundChannel | null = null;
  copied = false;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;
  private inboundChannelLoaded = false;

  procCount$: Observable<number> = this.store.select(
    (state) => (state.orders.orders ?? []).filter((order) => order.ato_processing_status === AtoProcessingStatus.processing).length,
  );
  errCount$: Observable<number> = this.store.select(
    (state) => (state.orders.orders ?? []).filter((order) => order.ato_processing_status === AtoProcessingStatus.needs_review).length,
  );
  showProcessing$: Observable<boolean> = this.store.select((state) => state.orders.showProcessing);
  total$: Observable<number> = combineLatest([this.procCount$, this.errCount$]).pipe(map(([proc, err]) => proc + err));
  active$: Observable<boolean> = this.total$.pipe(map((total) => total > 0));
  isDropdownOpen = false;
  currentLanguage: string = '';
  searchInput = new FormControl('');
  searchSupplierInput = new FormControl('');
  parsedFilter:
    | IInventoryFilterRequest
    | ICompanyFilterRequest
    | IQuotationFilterRequest
    | IOrderFilterRequest
    | IImportFilterRequest
    | ICatalogFilterRequest
    | IGroupFilterRequest
    | ISegmentFilterRequest
    | ISegmentFilterRequest
    | undefined = undefined;
  keyword: string = '';
  filterString: string = '';
  filtredQuotations: IQuotation[] = [];
  filters: any = {};
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1,
  };
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  modules: any = {
    inventory: 'inventory',
    companies: 'company',
    quotations: 'quotation',
    orders: 'order',
    catalogs: 'catalog',
    imports: 'import',
    brands: 'brand',
    tags: 'tag',
    categories: 'category',
    'companies-groups': 'companyGroup',
    segments: 'segment',
  };
  modulesMapping: Record<
    string,
    {
      component: any;
      refill: ((filter: any) => any) | null;
      filterSetter: (filter: any) => any;
    }
  > = {
    // inventory: {
    //   component: FilterDialogComponent,
    //   refill: refillInventoryFilter,
    //   filterSetter: SetInventoryFilter
    // },
    // company: {
    //   component: CustomersFiltersComponent,
    //   refill: refillCompanyFilter,
    //   filterSetter: SetCompanyFilter
    // },
    // quotation: {
    //   component: FilterQuotationsComponent,
    //   refill: refillQuotationFilter,
    //   filterSetter: SetQuotationFilter
    // },
    order: {
      component: OrdersFilterComponent,
      refill: refillOrdersFilter,
      filterSetter: SetOrdersFilter,
    },
    import: {
      component: ImportFilterComponent,
      refill: refillImportsFilter,
      filterSetter: SetImportFilter,
    },
    // catalog: {
    //   component: CatalogsFilterComponent,
    //   refill: refillCatalogsFilter,
    //   filterSetter: SetCatalogsFilter
    // },
    brand: {
      component: null,
      refill: null,
      filterSetter: SetBrandSearch,
    },
    tag: {
      component: null,
      refill: null,
      filterSetter: SetTagSearch,
    },
    category: {
      component: null,
      refill: null,
      filterSetter: SetCategoriesSearch,
    },
    // companyGroup: {
    //   component: FilterGroupComponent,
    //   refill: refillGroupFilter,
    //   filterSetter: SetGroupFilter
    // },
    segment: {
      component: FilterSegmentComponent,
      refill: refillSegmentFilter,
      filterSetter: SetSegmentFilter,
    },
  };
  module: string = '';
  filterSetter: any = null;
  params: any;
  private readonly addButtonPermissionMap: Record<string, HasPermissionContext> = {
    '/companies': { module: 'company', action: 'create' },
    '/companies-groups': { module: 'company', action: 'create' },
    '/organize/brands': { module: 'product', action: 'create' },
    '/organize/categories': { module: 'product', action: 'create' },
    '/organize/tags': { module: 'product', action: 'create' },
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private actions$: Actions,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private navigationService: NavigationService,
    private inboundChannelService: InboundChannelService,
  ) {
    this.navbarParams = {
      title: null,
      breadcrumbs: [],
      parentPath: null,
      icon: null,
    };
    this.module = this.currentModule();
    if (this.module) {
      const moduleConfig = this.modulesMapping[this.module];
      if (moduleConfig) {
        this.filterSetter = moduleConfig.filterSetter;
      }
    }
    this.currentUrl = window.location.pathname;

    this.route.queryParams.subscribe((params) => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.params = params;
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = window.location.pathname;
        this.totalElement = 0;
        const previousModule = this.module;
        this.module = this.currentModule();
        if (this.module) {
          const moduleConfig = this.modulesMapping[this.module];
          if (moduleConfig) {
            this.filterSetter = moduleConfig.filterSetter;
          }
        }
        this.dispatchFilter(this.params, previousModule !== this.module);
        this.clearInputs();
      }
    });
    this.isCopilot$ = this.store.select((state) => state.quotations.filterState.isCopilot);

    this.isCopilot$.subscribe((isCopilot) => {
      if (isCopilot) {
        this.isCopilotTitle = 'AI Copilot';
        this.isCopilot = true;
      } else {
        this.isCopilotTitle = '';
        this.isCopilot = false;
      }
    });
  }

  /**
   * @deprecated AI Copilot is dead code. Nothing in the UI turns the mode on —
   * the only way in is a hand-written `?isCopilot=1` — and since WB-7275 hid
   * this navbar on /quotations, nothing renders the control that turned it off
   * either. Kept only so the existing state plumbing stays consistent.
   *
   * Removal is tracked in WB-7763. It means deleting, together:
   *   - this method, `isCopilot$` / `isCopilot` / `isCopilotTitle`, the
   *     `?isCopilot=1` query-param handling, and the copilot title block in
   *     navbar.component.html
   *   - `SetIsCopilot` and `filterState.isCopilot` in the quotations store
   *   - the `*ngIf="!(isCopilot$ | async)"` gate on the Orders "New order"
   *     group, and the `th-copilot` / `td-copilot` column plus the copilot
   *     score cells in list-quotations
   *   - `DetailsCopilotScoreComponent` and the `IQuotationScore` plumbing, if
   *     nothing else consumes them
   */
  applyCopilot() {
    this.isCopilot$.pipe(take(1)).subscribe((isCopilot) => {
      this.store.dispatch(SetIsCopilot({ isCopilot: !isCopilot }));
    });
  }

  showNavbar() {
    if (!this.currentUrl) return true;

    // Hide for /companies (list) and /companies/:id (detail) without waiting for currentId.
    // Only show for /companies/add and sub-routes like /companies/:id/add-location/:cid.
    const segments = this.currentUrl.split('/').filter(Boolean);
    if (segments[0] === 'companies' && (segments.length === 1 || (segments.length === 2 && segments[1] !== 'add'))) {
      return false;
    }

    const fullHeightPages = [
      `/inventory/${this.currentId}`,
      `/quotations/${this.currentId}`,
      '/agents',
      '/agents/add',
      `/agents/${this.currentId}`,
      '/agents/new-catalog',
      '/catalogs/new-catalog',
      '/agents/new-catalog/add-products',
      '/catalogs/new-catalog/add-products',
      '/agents/commissions',
      '/agents/commissions/add',
      `/agents/commissions/${this.currentId}`,
      `/catalogs/edit-catalog/${this.currentId}`,
      `/catalogs/edit-catalog/${this.currentId}/add-products`,
      `/catalogs/agents-catalog/edit-catalog/${this.currentId}`,
      `/agents/edit-catalog/${this.currentId}/add-products`,
      `/agents/edit-catalog/${this.currentId}`,
      // These carry their own <ui-page-header>; the legacy bar would be a
      // second title above it (WB-7275).
      //
      // Hiding it on /quotations also removes the AI Copilot title block, which
      // held the only control that left Copilot mode. That is deliberate: the
      // feature is deprecated and has no UI entry point left — it is reachable
      // only by hand-writing `?isCopilot=1` — so there is nothing to exit from.
      // The mode should be deleted outright rather than given a new exit:
      // tracked in WB-7763, checklist on `applyCopilot()` below.
      '/quotations',
      '/inventory',
      '/catalogs',
      '/warehouses',
      '/pricebook',
      '/smart-inbox',
      '/settings',
      '/settings/profile',
      '/settings/security',
      '/settings/tips',
      '/settings/users',
      '/settings/teams',
      '/settings/roles',
      '/settings/roles-permissions',
      '/settings/language',
      '/settings/notifications',
      '/settings/warehouses',
      '/settings/custom-fields',
      '/settings/smart-inbox',
      '/knowledge-base',
      '/settings/orders',
      '/organize/brands',
      '/organize/categories',
      '/organize/tags',
      '/companies-groups',
      '/companies-groups/add',
      `/companies-groups/${this.currentId}`,
    ];

    if (this.currentUrl.startsWith('/settings/roles/')) {
      return false;
    }

    if (this.currentUrl.startsWith('/workflows')) {
      return false;
    }

    return !fullHeightPages.includes(this.currentUrl);
  }

  canShowSearchInput() {
    return [
      '/companies',
      '/comparators',
      '/imports',
      '/organize/tags',
      '/organize/brands',
      '/organize/categories',
      // '/companies-groups', // WB-5797: moved into feature
      '/segments',
      /*
      '/suppliers' */
    ].includes(this.router.url.split('?')[0]);
  }

  canDisplayAddButton() {
    return [
      '/companies',
      '/imports',
      '/comparators',
      '/organize/tags',
      '/organize/brands',
      '/organize/categories',
      // '/companies-groups', // WB-5797: moved into feature
      '/segments',
      '/agents',
    ].includes(this.router.url.split('?')[0]);
  }

  addButtonPermission(): HasPermissionContext {
    const routePath = this.router.url.split('?')[0];
    return this.addButtonPermissionMap[routePath] ?? {};
  }

  canShowAdvancedFilter() {
    return [
      '/companies',
      '/imports',
      // '/companies-groups', // WB-5797: moved into feature
      '/segments',
      /*
      '/suppliers' */
    ].includes(this.router.url.split('?')[0]);
  }

  searchPlaceholder() {
    switch (this.router.url.split('?')[0]) {
      case '/inventory':
        return 'inventory.search_prod';
      case '/quotations':
        return 'quotations_and_orders.search_quotation';
      case '/companies':
        return 'companies.search_a_customer';
      case '/orders':
        return 'quotations_and_orders.search_order';
      case '/segments':
        return 'segment.search_a_segment';
      case '/companies-groups':
        return 'companies_groups.search_a_group';
      case '/organize/tags':
        return 'Labels.search_label';
      case '/organize/brands':
        return 'brands.search_brand';
      case '/organize/categories':
        return 'Categories.search_category';
      case '/imports':
        return 'import.search_file';
      case '/suppliers':
        return 'common.search';
      default:
        return '';
    }
  }
  dispatchFilter(params: any, moduleChanged: boolean = true) {
    this.keyword = params['keyword'] ?? '';
    this.filters.keyword = this.keyword;
    if (params['filter']) {
      this.filterString = params['filter'];
      const decodedFilterParam = decodeURIComponent(this.filterString);
      try {
        const parsedFilterParam = JSON.parse(decodedFilterParam);
        this.filters.advanced = refillFilter(this.module, parsedFilterParam);
        this.parsedFilter = parseFilterRequest(this.module, this.filters.advanced);
      } catch (error) {
        this.filterString = '';
        this.parsedFilter = undefined;
        this.filters.advanced = refillFilter(this.module, null);
      }
    } else if (moduleChanged) {
      this.filterString = '';
      this.parsedFilter = undefined;
      this.filters.advanced = refillFilter(this.module, null);
    }
    this.searchInput.setValue(params['keyword'] ?? '');
    if (this.module != 'quotation') {
      this.isCopilot = false;
      this.store.dispatch(SetIsCopilot({ isCopilot: false }));
    } else {
      if (params['isCopilot'] && params['isCopilot'] === '1') {
        this.store.dispatch(SetIsCopilot({ isCopilot: true }));
      }
    }
    if (this.filterSetter) {
      this.store.dispatch(this.filterSetter(this.filters));
    }
  }

  currentModule() {
    const url = this.router.url;
    const segments = url.split('?')[0].split('/').filter(Boolean);
    if (segments[0] === 'organize') {
      return this.modules[`${segments[1] ?? 'brands'}`];
    }
    return this.modules[segments[0]];
  }

  /**
   * The subscriptions of 'valueChanges' should be refactored, there should be just one search input for all routes,
   * and just one subscription to handle the dispatch logic.
   */
  ngOnInit(): void {
    this.currentMetaData$ = this.store.select((state) => state.shared.currentMetaDat);

    const sub1 = this.actions$
      .pipe(
        ofType(SetCurrentElementId),
        tap((action) => {
          this.currentId = action.currentElementId!;
          console.log('CurrentId', this.currentId);
        }),
      )
      .subscribe();

    const sub2 = this.currentMetaData$.subscribe((currentMetaData) => {
      this.totalElement = currentMetaData?.count ? currentMetaData?.count : 0;
    });

    const sub3 = this.actions$
      .pipe(
        ofType(SetCurrentElementObject),
        tap((action) => {
          this.currentName = action.currentElementObject?.name!;
          this.currentCompanyId = action.currentElementObject?.company_id!;
          this.currentId = action.currentElementObject?.id!;
          this.currentCheck = action.currentElementObject?.is_main!;
        }),
      )
      .subscribe();

    const sub5 = this.searchInput.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((term) => {
          if (this.filterSetter && this.filters.keyword != term) {
            this.filters.keyword = term;
            this.store.dispatch(this.filterSetter(this.filters));
            let queryParams: any = {
              page: 1,
              count: this.page.size,
            };
            if (term != '') {
              queryParams.keyword = term;
            } else {
              queryParams.keyword = null;
            }
            this.router.navigate([], {
              relativeTo: this.navigationService.getCurrentRoute(),
              queryParams: { ...queryParams },
              queryParamsHandling: 'merge',
            });
          }
        }),
      )
      .subscribe();

    const sub28 = this.searchSupplierInput.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((term) => this.store.dispatch(SetSupplierSearch({ search: term! }))),
      )
      .subscribe();

    this.store
      .select((state) => state.shared.navbarParams)
      .subscribe((navbarParams) => {
        if (navbarParams) {
          this.navbarParams.title = navbarParams.title;
          this.navbarParams.breadcrumbs = navbarParams.breadcrumbs;
          this.navbarParams.parentPath = navbarParams.parentPath;
          this.navbarParams.icon = navbarParams.icon;
        }
      });
    this.subscriptions.push(sub1, sub2, sub3, sub5, sub28);

    if (this.currentUrl === '/orders' || this.currentUrl === '/quotations') {
      this.loadInboundChannel();
    }
  }

  navigateTo(url: string): void {
    if (this.router.url === '/catalogs/add') {
      const exitConfirmed = confirm(this.translateService.instant('Catalogs.confirmExit'));
      if (exitConfirmed) {
        this.router.navigate([url], {
          relativeTo: this.navigationService.getCurrentRoute(),
          queryParams: {
            page: this.page.number,
            count: this.page.size,
          },
        });
      }
    } else if (url !== null) {
      this.router.navigate([url], {
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: {
          page: this.page.number,
          count: this.page.size,
        },
      });
    }
  }

  hasModuleFilters(key?: string): boolean {
    if (this.parsedFilter) return this.parsedFilter.filters && this.parsedFilter?.filters?.length > 0;
    return false;
  }

  switchLanguage(lang: string) {
    if (lang !== this.translateService.currentLang) this.translateService.use(lang);
    localStorage.setItem('lang', lang);
    this.currentLanguage = lang;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  back() {
    if (this.currentUrl === '/catalogs/add') {
      const exitConfirmed = confirm(this.translateService.instant('Catalogs.confirmExit'));
      if (exitConfirmed) {
        this.router.navigate([this.navbarParams.parentPath], {
          relativeTo: this.navigationService.getCurrentRoute(),
          queryParams: {
            page: this.page.number,
            count: this.page.size,
          },
        });
      }
    } else {
      let queryParams: any = {
        page: this.page.number,
        count: this.page.size,
      };
      if (this.filterString && this.filterString != '') {
        queryParams.filter = this.filterString;
      }
      if (this.keyword && this.keyword != '') {
        queryParams.keyword = this.keyword;
      }
      this.router.navigate([this.navbarParams.parentPath], {
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: {
          ...queryParams,
        },
      });
    }
  }

  isOrganize(): boolean {
    if (this.currentUrl?.startsWith('/organize')) {
      return true;
    }
    return false;
  }

  hintTitle(): string {
    if (
      (this.currentUrl?.startsWith(`/add-location/${this.currentId}`) && this.currentName && this.currentCompanyId) ||
      (this.currentUrl?.startsWith(`/companies/${this.currentId}`) && this.currentCheck === false)
    ) {
      return this.translateService.instant('companies.this_is_a_seconde');
    }
    if (
      this.currentUrl?.startsWith('/companies/add') ||
      (this.currentUrl?.startsWith(`/companies/${this.currentId}`) && this.currentCheck === true)
    ) {
      return this.translateService.instant('companies.this_is_main_company_location');
    }
    return '';
  }

  showHint(): boolean {
    if (this.currentUrl?.startsWith(`/add-location/${this.currentId}`) && this.currentName && this.currentCompanyId) {
      return true;
    }
    if (this.currentUrl?.startsWith(`/companies/${this.currentId}`)) {
      return true;
    }
    if (this.currentUrl?.startsWith('/companies/add')) {
      return true;
    }
    return false;
  }

  //login filter and add btn

  add() {
    switch (this.currentUrl) {
      case '/inventory':
        this.addProduct();
        break;
      case '/organize/categories':
        this.addCategorie();
        break;
      case '/organize/tags':
        this.addTags();
        break;
      case '/organize/brands':
        this.addBrands();
        break;
      case '/organize':
        this.addBrands();
        break;
      case '/companies':
        this.addCustomers();
        break;
      // WB-5797: moved into feature
      // case '/companies-groups':
      //   this.addCompaniesGroups()
      //   break
      case '/imports':
        this.import();
        break;
      case '/comparators':
        this.importComparators();
        break;
      case '/segments':
        this.addSegments();
        break;
      case '/agents':
        this.addAgent();
        break;
      default:
        break;
    }
  }
  addProduct() {
    this.router.navigate(['inventory/add'], {
      queryParamsHandling: 'merge',
    });
  }

  setFilter(key?: string) {
    const moduleConfig = this.modulesMapping[this.module];
    if (moduleConfig) {
      const dialogRef = this.dialog.open(moduleConfig.component, {
        minWidth: '650px',
        width: '90vw',
        height: '80vh',
      });
      dialogRef.afterClosed().subscribe((filterResult) => {
        if (filterResult && moduleConfig.refill) {
          const unparsedFilter = moduleConfig.refill(filterResult);
          const encoded = encodeFilterObject(unparsedFilter, false);
          this.router.navigate([], {
            queryParams: {
              page: 1,
              filter: encoded,
            },
            queryParamsHandling: 'merge',
          });
        }
      });
    }
  }

  clearFilter(key?: string) {
    this.filters.advanced = refillFilter(this.module, null);
    if (this.filterSetter) {
      this.store.dispatch(this.filterSetter(this.filters));
    }
  }

  clearInputs() {
    if (this.searchSupplierInput.value) this.searchSupplierInput.setValue('');
  }

  addCustomers() {
    this.router.navigate(['companies/add'], {
      queryParamsHandling: 'merge',
    });
  }

  addAgent() {
    this.router.navigate(['agents/add'], {
      queryParamsHandling: 'merge',
    });
  }

  addCategorie() {
    this.dialog.open(AddCategoriesComponent, {
      disableClose: true,
      position: { right: '0px', top: '0px' },
      width: '50vw',
      height: '100vh',
      minWidth: '650px',
    });
  }

  addTags() {
    this.dialog.open(AddTagsComponent, {
      disableClose: true,
      position: { right: '0px', top: '0px' },
      width: '50vw',
      height: '100vh',
      minWidth: '650px',
    });
  }

  addBrands() {
    this.dialog.open(AddBrandsComponent, {
      disableClose: true,
      position: { right: '0px', top: '0px' },
      width: '50vw',
      height: '100vh',
      minWidth: '650px',
    });
  }

  // ── Inbound order channel control ──────────────────────────────────────────
  loadInboundChannel(): void {
    if (this.inboundChannelLoaded) return;
    this.inboundChannelLoaded = true;
    this.inboundChannelService
      .getInboundChannel()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel) => (this.inboundChannel = channel),
        error: () => (this.inboundChannelLoaded = false),
      });
  }

  onChannelClick(active: boolean): void {
    if (active) {
      this.toggleVisibility();
    } else {
      this.copyAddress();
    }
  }

  toggleVisibility(): void {
    this.store.dispatch(ToggleProcessingVisibility());
  }

  copyAddress(): void {
    const address = this.inboundChannel?.address ?? '';
    if (!address) return;
    // navigator.clipboard only exists in secure contexts (HTTPS / localhost);
    // over plain HTTP it is undefined, so fall back to execCommand.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(address).then(
        () => this.markCopied(),
        () => this.fallbackCopyAddress(address)
      );
    } else {
      this.fallbackCopyAddress(address);
    }
  }

  private markCopied(): void {
    this.copied = true;
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => (this.copied = false), 1600);
  }

  private fallbackCopyAddress(address: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = address;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      if (document.execCommand('copy')) {
        this.markCopied();
      }
    } catch {
      // Copy unsupported in this context — leave the indicator unchanged.
    } finally {
      document.body.removeChild(textarea);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copyTimer) clearTimeout(this.copyTimer);
  }

  // WB-5797: moved into feature
  // addCompaniesGroups() {
  //   this.router.navigate(['companies-groups/add'], {
  //     queryParamsHandling: 'merge',
  //   });
  // }

  thirdTitle() {
    if (this.currentUrl?.startsWith(`/edit-companies-groups`)) {
      return true;
    }
    switch (this.currentUrl) {
      case '/companies-groups/add':
        return true;

      case '/edit-companies-groups':
        return true;

      default:
        return false;
    }
  }

  DetailsQuotess() {
    this.router.navigate(['/details-quotes'], {
      queryParamsHandling: 'merge',
    });
  }

  enableLogo(): boolean {
    const routesToEnableLogo = ['/quotations/add'];

    return !routesToEnableLogo.some((route) => this.router.url.startsWith(route));
  }

  addQuotation() {
    this.router.navigate(['quotations/add'], {
      queryParamsHandling: 'merge',
    });
  }

  import() {
    this.dialog.open(ImportComponent, {
      disableClose: true,
      width: '35vw',
      minWidth: '600px',
    });
  }

  importComparators() {
    this.dialog.open(ImportComparatorsComponent, {
      disableClose: true,
      width: '35vw',
      minWidth: '600px',
    });
  }

  addSegments() {
    this.dialog.open(AddSegmentsComponent, {
      disableClose: true,
      position: { right: '0px', top: '0px' },
      width: '50vw',
      height: '100vh',
      minWidth: '650px',
    });
  }

  exportCustomers(): void {
    this.store.dispatch(exportCompanies({ filter: parseCompanyFilterRequest(this.filters.advanced), key: this.keyword }));
  }

  exportProducts(): void {
    this.store.dispatch(exportProducts({ filter: parseInventoryFilterRequest(this.filters.advanced), key: this.keyword }));
  }

  async exportQuotations(): Promise<void> {
    const copilot = await firstValueFrom(this.isCopilot$);
    this.store.dispatch(
      exportQuotation({ filter: parseQuotationFilterRequest(this.filters.advanced), key: this.keyword, co_pilot: copilot ? 1 : 0 }),
    );
  }

  exportOrders(): void {
    this.store.dispatch(exportOrders({ filter: parseOrdersFilterRequest(this.filters.advanced), key: this.keyword }));
  }
  isCatalogDetailsPage(): boolean {
    if (!this.currentUrl) return false;
    return this.currentUrl.startsWith('/catalogs/') && !this.currentUrl.includes('/add');
  }

  // openShareCatalog(): void {
  //   const urlParts = this.currentUrl?.split('/');
  //   const catalogId = urlParts && urlParts.length > 2 ? parseInt(urlParts[2], 10) : null;
  //   if (!catalogId) return;
  //   let catalogName = '';
  //   if (this.navbarParams.breadcrumbs && this.navbarParams.breadcrumbs.length > 0) {
  //     const lastBreadcrumb = this.navbarParams.breadcrumbs[this.navbarParams.breadcrumbs.length - 1];
  //     catalogName = lastBreadcrumb.label;
  //   }
  //   const dialogRef = this.dialog.open(ShareCatalogComponent, {
  //     width: '600px',
  //     disableClose: false,
  //     data: {
  //       catalogId: catalogId,
  //       catalogName: catalogName
  //     }
  //   });

  //   dialogRef.afterClosed().subscribe(result => {
  //     if (result && result.success) {
  //       this.dialog.open(ConfirmNotificationComponent, {
  //         width: '600px',
  //         data: {
  //           notifiedCount: result.notifiedCount
  //         }
  //       });
  //     }
  //   });
  // }
}
